import { TranslationKey } from "../../../../../modules/localization/translations"
import { CreateUpdateBookingResourceTableSetups } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { ResourceFiltersSelectedItem, ResourceItem } from "../../createOrderTypes"
import { TimelineCalendarResource } from "./calendar/fullCalendarTypes"

export const filterResources = (
    resources: TimelineCalendarResource[],
    filters: ResourceFiltersSelectedItem[],
    searchRegex: RegExp | undefined,
    activeResourceIds: readonly string[] | null,
): TimelineCalendarResource[] => {
    if (!filters.length && !searchRegex) return resources

    return resources
        .map((resource) => {
            const isPartOfActiveBooking = !!activeResourceIds?.includes(resource.id)

            const passesFilters = filters.every((filter) => {
                const filterValues = filter.values.map((v) => v.value)

                if (!filterValues.length) return true

                switch (filter.identifier) {
                    case "resourceTypeIds":
                        return resource.type ? filterValues.some((fv) => Number(fv) === resource.type?.id) : false
                    case "floors":
                        return resource.floor ? filterValues.includes(resource.floor) : false
                    case "fixedAvIds":
                        return filterValues.every((filterValue) =>
                            resource.avEquipments?.some((av) => av.id === filterValue),
                        )
                    case "sittings":
                        return resource.seats ? resource.seats >= Number(filterValues[0]) : false
                    case "serviceCenterIds": {
                        if (!resource.locationId) return false
                        const serviceCenterId = Number(resource.locationId.split("_")[0])
                        return filterValues.includes(serviceCenterId)
                    }
                    case "tableSetupIds": {
                        if (!resource.tableSetups || resource.tableSetups.length === 0) return false
                        return resource.tableSetups.some((ts) => filterValues.includes(ts.key))
                    }
                    default:
                        return true
                }
            })

            const passesSearch = searchRegex ? searchRegex.test(resource.title) : true

            const isDimmed = (!passesFilters || !passesSearch) && isPartOfActiveBooking

            if (!((passesFilters && passesSearch) || isPartOfActiveBooking)) return null

            // Avoid recreating resource objects unless we actually need to add `isDimmed`.
            return isDimmed ? { ...resource, isDimmed: true } : resource
        })
        .filter((resource) => resource !== null) as TimelineCalendarResource[]
}

type Group = {
    typeId: number
    typeName: TranslationKey
    resources: ResourceItem[]
}

const typeLocalizationMap: Record<number, TranslationKey> = {
    1: "create-order-confirm-booking-rooms",
    2: "create-order-confirm-booking-equipment",
    3: "create-order-confirm-booking-cars",
}

export function groupResourcesForRendering(resources: ResourceItem[]): Group[] {
    const groupMap = new Map<number, Group>()

    for (const resource of resources) {
        const { id: typeId } = resource.type

        if (!groupMap.has(typeId)) {
            groupMap.set(typeId, {
                typeId,
                typeName: typeLocalizationMap[typeId],
                resources: [],
            })
        }

        groupMap.get(typeId)!.resources.push(resource)
    }

    return Array.from(groupMap.values())
}

export function buildFinalResources(
    resourceIds: string[],
    resources: CreateUpdateBookingResourceTableSetups[],
): CreateUpdateBookingResourceTableSetups[] {
    const idSet = new Set(resourceIds.map(Number))
    const validResources = resources.filter((r) => idSet.has(r.id))

    const missingResources = Array.from(idSet)
        .filter((id) => !resources.some((r) => r.id === id))
        .map((id) => ({ id, tableSetups: [] }))

    return [...validResources, ...missingResources]
}

export function getMeetingLocationFromBookedResources(
    args: Readonly<{
        bookedResources: ReadonlyArray<CreateUpdateBookingResourceTableSetups> | null | undefined
        fullResourcesList: ReadonlyArray<ResourceItem> | null | undefined
    }>,
): string {
    const ids = (args.bookedResources ?? []).map((r) => r.id)
    if (ids.length === 0) return ""

    const byId = new Map((args.fullResourcesList ?? []).map((r) => [r.id, r.title]))
    return ids
        .map((id) => byId.get(id)?.trim() ?? "")
        .filter(Boolean)
        .join(", ")
}
